import { httpsCallable } from "firebase/functions";
import { unresolvedMessage, type DacVerdict, type DeferredReason } from "@ks-dac/shared";
import { getFirebaseFunctions } from "./firebase";
import { IS_MOCK } from "./env";

/**
 * Four arms, mirroring the server's `ResolveAddressResult`.
 *
 * `deferred` carries a token and NO message: the three misses it covers promise
 * manual follow-up, so the customer continues to step 2 and learns the outcome
 * at step 3. Returning a message here is what invited rendering it at step 1,
 * which ended the flow before any contact details existed to honour the promise.
 *
 * `out_of_state` and `outside_service_area` are the opposite: definitive answers
 * with no token, so they stop at step 1 and the message is the whole payload.
 * They are two arms rather than one because they say different things to the
 * customer and count as different things in the funnel.
 */
export type ResolveResponse =
  | { status: "resolved"; token: string }
  | { status: "deferred"; reason: DeferredReason; token: string }
  | { status: "out_of_state"; message: string }
  | { status: "outside_service_area"; message: string };

/** What step 2 sends. Mirrors `LeadPayload` minus the fields the server fills in. */
export type SubmitRequest = {
  token: string;
  submissionId: string;
  contact: { firstName: string; lastName: string; email: string; phone: string | null };
  consent: { contactConsent: true; contactConsentAt: string; consentVersion: string };
  source: { site: string; page: string; embed: "embed" | "standalone" };
  enteredAddress: string;
};

/**
 * Call step 1. Serves a canned response in mock mode.
 *
 * @throws Whatever the callable rejects with. Step 1 surfaces it as a retryable error.
 */
export async function resolveAddress(
  address: string,
  submissionId: string,
): Promise<ResolveResponse> {
  if (IS_MOCK) return mockResolve(address);
  const fns = getFirebaseFunctions()!;
  const call = httpsCallable<{ address: string; submissionId: string }, ResolveResponse>(
    fns,
    "resolveAddress",
    // Required by the server's consumeAppCheckToken: a limited-use token can be
    // consumed exactly once, which is what makes replay protection real.
    { limitedUseAppCheckTokens: true },
  );
  return (await call({ address, submissionId })).data;
}

/**
 * Call step 2. Serves a canned response in mock mode.
 *
 * @throws Whatever the callable rejects with. An expired token returns the customer
 *         to step 1 with their contact fields intact.
 */
export async function submitLead(req: SubmitRequest): Promise<DacVerdict> {
  if (IS_MOCK) return mockSubmit(req.enteredAddress);
  const fns = getFirebaseFunctions()!;
  const call = httpsCallable<SubmitRequest, DacVerdict>(fns, "submitLead", {
    limitedUseAppCheckTokens: true,
  });
  return (await call(req)).data;
}

// ---------------------------------------------------------------------------
// Mock mode. Exported so tests can drive it directly.
//
// The trigger table is in README.md, "Local development". It is a how-to for a
// reviewer, so it lives where a reviewer looks.
//
// Failure text comes from `@ks-dac/shared`'s `unresolvedMessage()`, never a second
// copy: that map is THE source of customer-facing failure text (ADR-0004).
//
// Two traps at this line. The out-of-area trigger matches PLACE NAMES here, where
// the server screens on county FIPS off the GEOID; a mock has no GEOID, and asking
// a reviewer to type a tract number would make the unhappy screen unreachable in
// practice, which is the one thing this mock exists to prevent. And `mockSubmit`
// re-reads the same triggers off the entered address, because the deferred token is
// the same opaque "mock-token" as the resolved one and carries nothing to dispatch on.
// ---------------------------------------------------------------------------

const OUT_OF_STATE_TRIGGERS = /\b(oh|ma|vt|ct|nj|pa)\b/;
const LOOKS_LIKE_NEW_YORK = /\bny\b|\bnew york\b/;

const MOCK_MISS_TRIGGERS: ReadonlyArray<readonly [needle: string, reason: DeferredReason]> = [
  ["nowhere", "address_not_found"],
  ["unavailable", "geocoder_unavailable"],
  ["notindataset", "tract_not_in_dataset"],
];

function missTrigger(address: string): DeferredReason | null {
  const a = address.toLowerCase();
  for (const [needle, reason] of MOCK_MISS_TRIGGERS) {
    if (a.includes(needle)) return reason;
  }
  return null;
}

/**
 * Long Island and five-boroughs place names, for the mock only.
 *
 * Word-boundary matched: "babylon" would otherwise be a substring problem waiting
 * to happen, and unanchored "queens" hits "Queensbury", which is in Warren County
 * and squarely inside the service area.
 */
const OUT_OF_AREA_TRIGGERS =
  /\b(brooklyn|bronx|manhattan|queens|staten island|nassau|suffolk|hempstead|mineola|hicksville|islip|babylon|huntington|long island city|far rockaway)\b/;

/** Mock step 1. The trigger table is in README.md, "Local development". */
export async function mockResolve(address: string): Promise<ResolveResponse> {
  const a = address.toLowerCase();
  const deferred = missTrigger(address);
  if (deferred !== null) return { status: "deferred", reason: deferred, token: "mock-token" };
  if (!LOOKS_LIKE_NEW_YORK.test(a) && (OUT_OF_STATE_TRIGGERS.test(a) || a.includes("cleveland"))) {
    return { status: "out_of_state", message: unresolvedMessage("out_of_state") };
  }
  // After out_of_state, mirroring the server's gate order in resolver.ts: an
  // address naming both another state and a borough is out of state first.
  if (OUT_OF_AREA_TRIGGERS.test(a)) {
    return {
      status: "outside_service_area",
      message: unresolvedMessage("outside_service_area"),
    };
  }
  return { status: "resolved", token: "mock-token" };
}

/** The step-3 unresolved screen, which a deferred address now reaches. */
/** Mock step 2 for a deferred token, dispatching on the reason directly. */
export async function mockSubmitDeferred(reason: DeferredReason): Promise<DacVerdict> {
  return { status: "unresolved", reason, message: unresolvedMessage(reason) };
}

/**
 * Trigger for the NON-DESIGNATED arm.
 *
 * Added 2026-08-05, when that arm stopped being a bare "no" and started carrying
 * reviewed legal copy (the income-route sentence). Before this there was no way to
 * reach it without a backend, which is the same gap that let it go unreviewed and
 * unscanned on a live domain until 2026-07-30. A screen carrying a claim that
 * needs sign-off has to be reachable by the person signing it off.
 *
 * A real non-DAC tract from the pinned artifact, not an inverted flag on the
 * Menands fixture: Saratoga Springs 36091060101 is genuinely not designated, so
 * the percentiles and the place name stay coherent with each other.
 */
const NOT_A_DAC_TRIGGER = "notadac";

/** Mock step 2. Re-reads the triggers off the entered address; the mock token carries nothing. */
export async function mockSubmit(enteredAddress = ""): Promise<DacVerdict> {
  const deferred = missTrigger(enteredAddress);
  if (deferred !== null) return mockSubmitDeferred(deferred);

  if (enteredAddress.toLowerCase().includes(NOT_A_DAC_TRIGGER)) {
    return {
      status: "resolved",
      geocoder: "census",
      tract: {
        geoid: "36091060101",
        isDac: false,
        county: "Saratoga",
        cityTown: "Saratoga Springs city",
        region: "ROS",
        urbanRural: "suburban",
        percentile: { statewide: 12.4, nyc: 0, ros: 23.1744 },
      },
      vintage: {
        datasetId: "2e6c-s6fp",
        label: "Final Disadvantaged Communities (DAC) 2023",
        checksum: "349591734eef7b07a6ac6ddf3f77ae3e2dc8d69ae62c7f73c257a4f71d6c7aa2",
      },
    };
  }

  // The Menands office fixture, so the mock matches the pinned test data.
  return {
    status: "resolved",
    geocoder: "census",
    tract: {
      geoid: "36001013602",
      isDac: true,
      county: "Albany",
      cityTown: "Menands village",
      region: "ROS",
      urbanRural: "suburban",
      percentile: { statewide: 75.3086, nyc: 0, ros: 86.848 },
    },
    vintage: {
      datasetId: "2e6c-s6fp",
      label: "Final Disadvantaged Communities (DAC) 2023",
      checksum: "349591734eef7b07a6ac6ddf3f77ae3e2dc8d69ae62c7f73c257a4f71d6c7aa2",
    },
  };
}
